/**
 * The pieces both reading strips are built from -- the one for an arrangement kept from the
 * score's own notes (`ArrangeReading`) and the one for an arrangement started from its tune
 * (`TunedReading`): what was read, a figure on the strip, the disclosure that opens the words behind it, the
 * notes on what was read, and what the checks found.
 */

import type { TunedSource } from "@app/shared-types";
import { signature } from "../lib/arrange-words";
import { count } from "../lib/format";

/** Problems printed before the rest are counted off, so one bad roster cannot fill the page. */
const NOTES = 8;

/** One number on the strip, named quietly. */
export function Figure({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: string;
  tone?: string;
  title?: string;
}): JSX.Element {
  return (
    <span className="flex items-baseline gap-1.5" title={title}>
      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-shell-dim">
        {label}
      </span>
      <span className={`font-mono text-[12px] ${tone ?? "text-shell-lit"}`}>{value}</span>
    </span>
  );
}

/** The passage as it was read: its bars, metre, key and tempo, with its tempo changes and
 *  hairpins counted. Either start reads these the same way. */
export function Read({ source }: { readonly source: TunedSource }): JSX.Element {
  return (
    <>
      <Figure label="Bars" value={`${source.first}–${source.last}`} />
      <Figure label="Metre" value={source.metre} />
      <Figure label="Key" value={signature(source.fifths)} />
      <Figure
        label="Tempo"
        value={
          source.tempo === null
            ? "none"
            : `♩ = ${Math.round(source.tempo)}${
                source.tempoChanges === 0 ? "" : `, +${count(source.tempoChanges)}`
              }${source.hairpins === 0 ? "" : `, ${count(source.hairpins)} ↗`}`
        }
      />
    </>
  );
}

/** Opens the words behind the strip; `speak` lights it where they hold something to read. */
export function WhoPlaysWhat({
  open,
  speak,
  onOpen,
}: {
  readonly open: boolean;
  readonly speak: boolean;
  onOpen(open: boolean): void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onOpen(!open)}
      aria-expanded={open}
      className="ml-auto flex h-6 items-center gap-1 rounded px-2 text-[11px] text-shell-text hover:bg-shell-hot hover:text-shell-lit"
    >
      Who plays what
      {speak ? <span className="h-1.5 w-1.5 rounded-full bg-shell-live" /> : null}
      <span aria-hidden="true" className="text-shell-dim">
        {open ? "▾" : "▸"}
      </span>
    </button>
  );
}

/** What was read and done, each said as the pipeline says it, made into sentences. */
export function Told({ told }: { readonly told: readonly string[] }): JSX.Element | null {
  if (told.length === 0) return null;
  return (
    <p className="rounded border-l-2 border-unread bg-shell-key/60 px-2 py-1.5">
      {told.map((one) => `${one[0]?.toUpperCase() ?? ""}${one.slice(1)}.`).join(" ")}
    </p>
  );
}

/** What the checks found, the first {@link NOTES} of it and a count of the rest. */
export function Problems({ notes }: { readonly notes: readonly string[] }): JSX.Element | null {
  if (notes.length === 0) return null;
  return (
    <div className="rounded border-l-2 border-fault bg-shell-key/60 px-2 py-1.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-shell-dim">
        What the checks found
      </div>
      <ul className="mt-1 space-y-0.5">
        {notes.slice(0, NOTES).map((one, at) => (
          <li key={at}>· {one}</li>
        ))}
      </ul>
      {notes.length > NOTES ? (
        <p className="mt-1 font-mono text-[10px] text-shell-dim">
          and {count(notes.length - NOTES)} more
        </p>
      ) : null}
    </div>
  );
}
